<script setup>
import { ref } from 'vue'
import { Head, useForm, router } from '@inertiajs/vue3'
import { Plus, KeyRound, Trash2 } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'
import Field from '../../Components/Admin/Field.vue'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  staff: { type: Array, required: true },
})

const showForm = ref(false)
const novo = useForm({ staff_user: { name: '', email: '', password: '' } })
const senhaDe = ref(null)
const senha = useForm({ password: '' })

const criar = () =>
  novo.post('/admin/equipe', {
    onSuccess: () => {
      novo.reset()
      showForm.value = false
    },
  })

const redefinir = (id) =>
  senha.patch(`/admin/equipe/${id}/senha`, {
    onSuccess: () => {
      senha.reset()
      senhaDe.value = null
    },
  })

const remover = (pessoa) => {
  if (confirm(`Remover a conta de ${pessoa.name}?`)) router.delete(`/admin/equipe/${pessoa.id}`)
}
</script>

<template>
  <Head title="Equipe · Administração" />

  <div class="flex flex-wrap items-center gap-4">
    <div>
      <h1 class="text-xl font-semibold tracking-tight text-up-navy">Equipe</h1>
      <!-- Contas são por unidade porque cada unidade é um banco separado. -->
      <p class="mt-1 text-sm text-slate-500">
        Uma conta por pessoa — é o que permite saber quem publicou cada arquivo.
        As contas valem só nesta unidade.
      </p>
    </div>
    <button
      type="button"
      class="ml-auto flex items-center gap-2 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark"
      @click="showForm = !showForm"
    >
      <Plus class="h-4 w-4" aria-hidden="true" />
      Nova conta
    </button>
  </div>

  <p v-if="novo.errors.base" role="alert" class="mt-5 rounded-lg bg-up-orange/10 px-4 py-3 text-sm text-up-orange-dark">
    {{ novo.errors.base }}
  </p>

  <form v-if="showForm" class="mt-6 rounded-card bg-white p-6 shadow-card" @submit.prevent="criar">
    <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Cadastrar conta</h2>
    <div class="mt-5 grid gap-4 sm:grid-cols-3">
      <Field id="s-name" v-model="novo.staff_user.name" label="Nome" required :error="novo.errors.name" />
      <Field id="s-email" v-model="novo.staff_user.email" label="E-mail" type="email" required :error="novo.errors.email" />
      <Field id="s-pw" v-model="novo.staff_user.password" label="Senha" type="password" required hint="Mínimo de 8 caracteres." :error="novo.errors.password" />
    </div>
    <div class="mt-5 flex gap-3">
      <button
        type="submit"
        :disabled="novo.processing"
        class="rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
      >
        {{ novo.processing ? 'Salvando…' : 'Cadastrar' }}
      </button>
      <button type="button" class="rounded-lg px-4 py-2.5 text-sm text-slate-500 hover:text-up-navy" @click="showForm = false">
        Cancelar
      </button>
    </div>
  </form>

  <ul class="mt-6 divide-y divide-slate-100 overflow-hidden rounded-card bg-white shadow-card">
    <li v-for="pessoa in props.staff" :key="pessoa.id" class="px-5 py-4">
      <div class="flex flex-wrap items-center gap-3">
        <div class="min-w-0">
          <p class="truncate font-medium text-up-navy">
            {{ pessoa.name }}
            <span v-if="pessoa.isSelf" class="ml-1 rounded bg-up-bg px-1.5 py-0.5 text-[11px] text-slate-500">você</span>
          </p>
          <p class="truncate text-xs text-slate-500">{{ pessoa.email }}</p>
        </div>
        <div class="ml-auto flex items-center gap-1">
          <button
            type="button"
            class="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-up-navy"
            :aria-label="`Redefinir senha de ${pessoa.name}`"
            @click="senhaDe = senhaDe === pessoa.id ? null : pessoa.id"
          >
            <KeyRound class="h-4.5 w-4.5" />
          </button>
          <button
            v-if="!pessoa.isSelf"
            type="button"
            class="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-up-orange"
            :aria-label="`Remover ${pessoa.name}`"
            @click="remover(pessoa)"
          >
            <Trash2 class="h-4.5 w-4.5" />
          </button>
        </div>
      </div>

      <form v-if="senhaDe === pessoa.id" class="mt-4 flex flex-wrap items-end gap-3" @submit.prevent="redefinir(pessoa.id)">
        <div class="min-w-[16rem] flex-1">
          <Field :id="`pw-${pessoa.id}`" v-model="senha.password" label="Nova senha" type="password" required hint="Mínimo de 8 caracteres." :error="senha.errors.password" />
        </div>
        <button
          type="submit"
          :disabled="senha.processing"
          class="rounded-lg border border-up-orange px-4 py-2.5 text-sm font-semibold text-up-orange transition-colors hover:bg-up-orange/5 disabled:opacity-60"
        >
          Redefinir
        </button>
      </form>
    </li>
  </ul>
</template>
